import { CART_ERROR, CART_UPDATED, VARIANT_CHANGED, EVENTS } from 'events';

const DEV = (() => {
  try {
    const host = window.location.hostname;
    return (
      (window.Shopify && window.Shopify.designMode === true) ||
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host.endsWith('.shopifypreview.com') ||
      window.localStorage.getItem('theme:debug') === '1'
    );
  } catch (_e) {
    return false;
  }
})();

class EventBusClass {
  constructor() {
    this._handlers = new Map();
  }

  on(event, callback) {
    if (!this._handlers.has(event)) {
      this._handlers.set(event, new Set());
    }
    this._handlers.get(event).add(callback);
    return () => this.off(event, callback);
  }

  off(event, callback) {
    const set = this._handlers.get(event);
    if (!set) return;
    set.delete(callback);
    if (set.size === 0) this._handlers.delete(event);
  }

  emit(event, payload) {
    if (DEV) console.debug('[bus]', event, payload);
    const set = this._handlers.get(event);
    if (!set) return;
    for (const callback of [...set]) {
      try {
        callback(payload);
      } catch (error) {
        console.error('[bus] handler error:', event, error);
      }
    }
  }
}

export const EventBus = new EventBusClass();

class StoreClass {
  constructor() {
    this._state = {
      cart: null,
      currentVariant: null,
    };
  }

  get() {
    return { ...this._state };
  }

  setCart(cart) {
    this._state.cart = cart;
    EventBus.emit(CART_UPDATED, cart);
  }

  setVariant(variant) {
    this._state.currentVariant = variant;
    EventBus.emit(VARIANT_CHANGED, variant);
  }
}

export const Store = new StoreClass();

export class BaseComponent extends HTMLElement {
  constructor() {
    super();
    this._cleanups = [];
  }

  connectedCallback() {

  }

  disconnectedCallback() {
    this._cleanups.forEach((cleanup) => cleanup());
    this._cleanups = [];
  }

  listen(event, callback) {
    const unsubscribe = EventBus.on(event, callback);
    this._cleanups.push(unsubscribe);
    return unsubscribe;
  }

  on(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    this._cleanups.push(() => target.removeEventListener(type, handler, options));
  }
}

export function define(name, ComponentClass) {
  if (!customElements.get(name)) {
    customElements.define(name, ComponentClass);
  }
}

function routeRoot() {
  const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
  return root || '/';
}

function buildUrl(path, params) {
  const root = routeRoot();
  const base = root.endsWith('/') ? root : `${root}/`;
  const url = new URL(base + String(path).replace(/^\//, ''), window.location.origin);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) url.searchParams.set(key, value);
    });
  }
  return url.toString();
}

async function request(url, options = {}) {
  const response = await fetch(url, { credentials: 'same-origin', ...options });
  if (!response.ok) {
    let data = null;
    try {
      data = await response.clone().json();
    } catch (_e) {

    }
    const error = new Error((data && data.description) || response.statusText || 'Request failed');
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return response;
}

function jsonPost(body) {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  };
}

async function cartRequest(endpoint, payload) {
  try {
    const response = await request(buildUrl(`cart/${endpoint}.js`), jsonPost(payload));
    return response.json();
  } catch (error) {
    EventBus.emit(CART_ERROR, error);
    throw error;
  }
}

export const api = {

  addItem(payload) {
    return cartRequest('add', payload);
  },

  changeItem(payload) {
    return cartRequest('change', payload);
  },

  updateCart(payload) {
    return cartRequest('update', payload);
  },

  async getCart() {
    try {
      const response = await request(buildUrl('cart.js'), { headers: { Accept: 'application/json' } });
      return response.json();
    } catch (error) {
      EventBus.emit(CART_ERROR, error);
      throw error;
    }
  },

  async renderSection(sectionId, { url, params } = {}) {
    const target = new URL(url || window.location.href, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined) target.searchParams.set(key, value);
      });
    }
    const response = await request(target.toString(), { headers: { Accept: 'text/html' } });
    return response.text();
  },

  async renderProductCard(handle) {
    const response = await request(buildUrl(`products/${handle}`, { view: 'card' }), {
      headers: { Accept: 'text/html' },
    });
    return response.text();
  },

  async predictiveSearch(query, { types = 'product', limit = 6, extraParams } = {}) {
    const params = {
      q: query,
      'resources[type]': types,
      'resources[limit]': limit,
      ...extraParams,
    };
    const response = await request(buildUrl('search/suggest.json', params), {
      headers: { Accept: 'application/json' },
    });
    return response.json();
  },

  async predictiveSearchSection(query, sectionId, { types = 'product', limit = 6, unavailable } = {}) {
    const params = {
      q: query,
      'resources[type]': types,
      'resources[limit]': limit,
      section_id: sectionId,
    };
    if (unavailable) params['resources[options][unavailable_products]'] = unavailable;
    const response = await request(buildUrl('search/suggest', params), { headers: { Accept: 'text/html' } });
    return response.text();
  },
};

export function prefersReducedMotion() {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Bir elemanın kapsayıcı içindeki yatay konumu. scrollIntoView yerine kullanılır:
// o, sayfayı dikey de kaydırıyor — burada yalnız kapsayıcının kendi scroll'u oynar.
export function offsetWithin(container, element) {
  return container.scrollLeft + element.getBoundingClientRect().left - container.getBoundingClientRect().left;
}

// Kapsayıcıyı yatayda konumlandır. Arka plandaki (görünmeyen) sekmede render lifecycle
// kısılır: behavior:'smooth' animasyonu hiç başlamaz ve kapsayıcı yerinde kalır →
// hareket başlamadıysa anında konumlandır. Doğruluk animasyondan önce gelir.
const scrollFallbacks = new WeakMap();

export function scrollContainerTo(container, left, options = {}) {
  const target = Math.max(0, left);
  if (options.instant || prefersReducedMotion()) {
    container.scrollTo({ left: target, behavior: 'instant' });
    return;
  }

  const start = container.scrollLeft;
  container.scrollTo({ left: target, behavior: 'smooth' });
  if (Math.abs(start - target) <= 1) return;

  clearTimeout(scrollFallbacks.get(container));
  scrollFallbacks.set(
    container,
    setTimeout(() => {
      if (container.scrollLeft === start) container.scrollTo({ left: target, behavior: 'instant' });
    }, 120)
  );
}

let _scrollLockCount = 0;
export function lockScroll() {
  _scrollLockCount += 1;
  if (_scrollLockCount > 1) return;
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  document.body.style.overflow = 'hidden';
  if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
  // Marker for CSS to tuck 3rd-party floating widgets (chat/accessibility) under the overlay.
  document.documentElement.setAttribute('data-overlay-active', '');
}

export function unlockScroll() {
  if (_scrollLockCount === 0) return;
  _scrollLockCount -= 1;
  if (_scrollLockCount > 0) return;
  document.body.style.overflow = '';
  document.body.style.paddingRight = '';
  document.documentElement.removeAttribute('data-overlay-active');
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  'iframe',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(', ');

export function focusTrap(element) {
  const previouslyFocused = document.activeElement;

  function getFocusable() {
    return Array.from(element.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement
    );
  }

  function onKeydown(event) {
    if (event.key !== 'Tab') return;
    const focusable = getFocusable();
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  element.addEventListener('keydown', onKeydown);
  const focusable = getFocusable();

  (focusable[0] || element).focus({ preventScroll: true });

  return function release() {
    element.removeEventListener('keydown', onKeydown);
    if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
      previouslyFocused.focus();
    }
  };
}

export function announce(message, { assertive = false } = {}) {
  const region = document.getElementById('a11y-announcer');
  if (!region) return;
  region.setAttribute('aria-live', assertive ? 'assertive' : 'polite');
  region.textContent = '';

  window.requestAnimationFrame(() => {
    region.textContent = message;
  });
}

/**
 * The box maths, as pure functions.
 *
 * Lives in core, not in the calculator component, for two reasons. The Projects
 * app needs the same rules to change an amount from the saved-projects drawer,
 * where there is no product form to read — and the drawer opens on every page,
 * while the calculator component is only imported on product pages. A second
 * copy over there would drift from this one the first time a rule changed, and
 * the two would quietly quote different numbers for the same tile.
 *
 * `meta` is any object carrying mode / boxSqft / minBoxes / stepBoxes / ppb /
 * isArea — which the component instance itself does, so it passes `this`.
 */
export const QtyMath = {
  // Whole sellable boxes: never below the minimum, always on the step.
  snapBoxes(meta, boxes) {
    const step = meta.stepBoxes > 0 ? meta.stepBoxes : 1;
    const min = meta.minBoxes > 0 ? meta.minBoxes : 1;
    let v = boxes < min ? min : boxes;
    v = Math.ceil(v / step) * step;
    return Math.ceil(Math.round(v * 1e6) / 1e6);
  },

  // What name="quantity" submits for a whole-box count.
  //  unit mode → product_units: area = covered sq ft rounded UP; else pieces.
  //  box mode  → the box count itself.
  cartQty(meta, boxes) {
    if (meta.mode !== 'unit') return boxes;
    if (meta.isArea) return Math.ceil(Math.round(boxes * meta.boxSqft * 1e6) / 1e6);
    return Math.round(boxes * (meta.ppb > 0 ? meta.ppb : 1));
  },

  covered(meta, boxes) {
    return boxes * (meta.boxSqft > 0 ? meta.boxSqft : 1);
  },

  // Boxes behind an amount that is ALREADY whole-box coverage — the inverse of
  // `covered`, not the same question `fromArea` answers.
  //
  // It rounds where `fromArea` ceils, and that difference is the whole point:
  // a saved coverage is stored to two decimals, so 25 boxes comes back as
  // 138.89 rather than 138.8890, and ceil(138.89 / 5.55556) = ceil(25.0002)
  // reads as 26. Measured, not theorised: it made the saved-projects stepper
  // add two boxes per tap.
  boxesFor(meta, coveredSqft) {
    if (!(meta.boxSqft > 0)) return QtyMath.snapBoxes(meta, coveredSqft);
    return QtyMath.snapBoxes(meta, Math.round(coveredSqft / meta.boxSqft));
  },

  // Needed area -> the whole-box answer, the same one the product page gives.
  fromArea(meta, sqft) {
    const raw = meta.boxSqft > 0 ? sqft / meta.boxSqft : sqft;
    const boxes = QtyMath.snapBoxes(meta, raw);
    return { boxes, cartQty: QtyMath.cartQty(meta, boxes), covered: QtyMath.covered(meta, boxes) };
  },

  fromBoxes(meta, boxes) {
    const snapped = QtyMath.snapBoxes(meta, boxes);
    return {
      boxes: snapped,
      cartQty: QtyMath.cartQty(meta, snapped),
      covered: QtyMath.covered(meta, snapped),
    };
  },

  /* ---- The one rule ---------------------------------------------------------------
   * Everything that decides how much of a product goes into a cart is below, and every
   * surface asks it: the product page calculator, the cart line and drawer, the card and
   * cross-sell "Add", quick view, the Projects app and Inspire. Liquid only prints the raw
   * facts (snippets/sell-meta.liquid). Change a rule here and all of them change with it.
   * Order #38147 is why: the rule lived in four copies, one was changed, the others were not,
   * and a customer paid for 3 pebble pieces while the page showed three boxes.
   */

  // Raw facts -> how this variant is sold.
  sellMeta(raw) {
    const r = raw || {};
    const num = (x) => {
      const n = parseFloat(x);
      return Number.isFinite(n) ? n : 0;
    };
    const mode = r.mode || 'box';
    const sellUnit = r.productUnit || 'unit';
    const variantUnit = r.variantUnit || 'item';
    const isArea = /sq|ft|m²/i.test(sellUnit);
    const coverage = num(r.package) || 1;
    const covPiece = num(r.covPiece);
    // Pieces multiply into a box only when a piece has a coverage (a tile, a mosaic, pebble).
    // Sealer, grout, moulding and vanity carry a carton size too, but sell one at a time.
    const isTile = covPiece > 0;
    let ppb = num(r.piecesPerBox) || 1;
    if (ppb <= 0 || !isTile) ppb = 1;
    const boxCov = num(r.boxCoverage);
    let boxSqft = boxCov > 0 ? boxCov : mode === 'unit' && covPiece > 0 ? ppb * covPiece : coverage;
    if (!(boxSqft > 0)) boxSqft = coverage || 1;
    const minBoxes = Math.ceil(num(r.minBuy) || 1);
    const stepBoxes = Math.ceil(num(r.quantityBox) || 1);
    const unitOne = ppb > 1 ? 'box' : variantUnit;
    const meta = {
      variantId: r.v,
      price: num(r.price),
      mode,
      sellUnit,
      variantUnit,
      isArea,
      isTile,
      coverage,
      covPiece,
      ppb,
      boxSqft,
      hasBox: boxCov > 0 || ppb > 1,
      minBoxes,
      stepBoxes,
      unitOne,
      unitMany: /[xs]$/.test(unitOne) ? `${unitOne}es` : `${unitOne}s`,
      // The order line says "Boxes: N" only when the count really is boxes.
      boxesProp: mode === 'unit' && unitOne === 'box',
      maxUnits: null,
      maxBoxes: 0,
      lowStock: false,
    };
    // Tracked stock that cannot be oversold caps the count at the whole boxes it fills.
    // Less than one full box left is sold as the loose remainder, and said so.
    if (r.tracked && Number.isFinite(parseFloat(r.stock))) {
      meta.maxUnits = Math.max(0, num(r.stock));
      let n = Math.floor(meta.maxUnits / QtyMath.perCount(meta) + 1e-9);
      while (n > 0 && QtyMath.cartQty(meta, n) > meta.maxUnits) n--;
      n = Math.floor(n / stepBoxes) * stepBoxes;
      if (n >= minBoxes) meta.maxBoxes = n;
      else if (meta.maxUnits > 0) meta.lowStock = true;
    }
    return meta;
  },

  // Cart units one count stands for (sq ft in a box, pieces in a box, or 1).
  perCount(meta) {
    if (meta.mode !== 'unit') return 1;
    if (meta.isArea) return meta.boxSqft;
    return meta.ppb > 0 ? meta.ppb : 1;
  },

  // A count of boxes -> exactly what goes into the cart.
  line(meta, rawBoxes) {
    if (meta.lowStock) return QtyMath._loose(meta, meta.maxUnits);
    let boxes = QtyMath.snapBoxes(meta, rawBoxes == null ? meta.minBoxes : rawBoxes);
    const capped = meta.maxBoxes > 0 && boxes > meta.maxBoxes;
    if (capped) boxes = meta.maxBoxes;
    const quantity = QtyMath.cartQty(meta, boxes);
    const covered = QtyMath.covered(meta, boxes);
    return {
      loose: false,
      capped,
      boxes,
      quantity,
      covered,
      // The cart holds whole sq ft: a 5.56 sq ft box is billed as 6.
      billed: meta.mode === 'unit' && meta.isArea && Math.abs(quantity - covered) > 0.001,
      properties: meta.boxesProp ? { Boxes: String(boxes) } : null,
      atMax: meta.maxBoxes > 0 && boxes >= meta.maxBoxes,
    };
  },

  _loose(meta, quantity) {
    const covered = meta.isArea ? quantity : Math.round(quantity * (meta.covPiece || 0) * 100) / 100;
    return { loose: true, capped: false, boxes: null, quantity, covered, billed: false, properties: null, atMax: quantity >= meta.maxUnits };
  },

  // A quantity already in the cart -> the same answer, for the cart line and its stepper.
  forCart(meta, quantity) {
    if (meta.lowStock) return QtyMath._loose(meta, quantity);
    const s = QtyMath.line(meta, Math.max(1, Math.round(quantity / QtyMath.perCount(meta))));
    s.quantity = quantity;
    s.billed = meta.mode === 'unit' && meta.isArea && Math.abs(quantity - s.covered) > 0.001;
    return s;
  },

  // The quantity the cart stepper moves to: one step of whole boxes, never past the stock.
  stepCart(meta, quantity, direction) {
    if (meta.lowStock) return Math.min(meta.maxUnits, Math.max(1, quantity + direction));
    const s = QtyMath.forCart(meta, quantity);
    const boxes = Math.max(meta.minBoxes, s.boxes + direction * meta.stepBoxes);
    return QtyMath.line(meta, boxes).quantity;
  },

  // A typed cart quantity -> the whole-box quantity that covers it.
  snapCart(meta, quantity) {
    if (meta.lowStock) return Math.min(meta.maxUnits, Math.max(1, Math.round(quantity)));
    return QtyMath.line(meta, Math.ceil(quantity / QtyMath.perCount(meta) - 1e-9)).quantity;
  },

  // "3 boxes · 15 pieces · 15 sq ft", "3 boxes covers 20 sq ft · billed as 21 sq ft".
  describe(meta, s, extra = '') {
    const n = (x) => (Number.isInteger(x) ? String(x) : String(Math.round(x * 100) / 100));
    const pieces = (c) => (c === 1 ? meta.sellUnit : `${meta.sellUnit}s`);
    if (s.loose) {
      if (meta.isArea) return `${n(s.quantity)} sq ft`;
      return meta.covPiece > 0 ? `${n(s.quantity)} ${pieces(s.quantity)} · ${n(s.covered)} sq ft` : `${n(s.quantity)} ${pieces(s.quantity)}`;
    }
    const word = s.boxes === 1 ? meta.unitOne : meta.unitMany;
    if (meta.mode === 'unit' && !meta.isArea) {
      if (!meta.isTile) return '';
      if (meta.ppb > 1) {
        const p = Math.round(s.boxes * meta.ppb);
        return `${n(s.boxes)} ${word} · ${p} ${pieces(p)} · ${n(s.covered)} sq ft${extra}`;
      }
      return `${n(s.boxes)} ${word} · ${n(s.covered)} sq ft${extra}`;
    }
    return `${n(s.boxes)} ${word} covers ${n(s.covered)} sq ft${extra}${s.billed ? ` · billed as ${n(s.quantity)} sq ft` : ''}`;
  },

  stockMessage(meta, s) {
    const n = (x) => (Number.isInteger(x) ? String(x) : String(Math.round(x * 100) / 100));
    if (meta.lowStock) {
      const unit = meta.isArea ? 'sq ft' : meta.maxUnits === 1 ? meta.sellUnit : `${meta.sellUnit}s`;
      return meta.unitOne === 'box'
        ? `Only ${n(meta.maxUnits)} ${unit} left, less than one full box.`
        : `Only ${n(meta.maxUnits)} ${unit} left.`;
    }
    if (s && s.capped) return `Only ${n(meta.maxBoxes)} ${meta.maxBoxes === 1 ? meta.unitOne : meta.unitMany} in stock.`;
    return '';
  },

  cartNote(meta, s) {
    if (meta.lowStock) return meta.unitOne === 'box' ? 'Less than one full box left. This is all we have.' : 'That is all we have in stock.';
    if (s && s.atMax) return 'That is all we have in stock.';
    return '';
  },
};

const a11y = { focusTrap, lockScroll, unlockScroll, prefersReducedMotion, announce };
const theme = { EventBus, Store, BaseComponent, define, api, a11y, events: EVENTS, QtyMath };

// Published unconditionally, unlike `window.theme` below: this is a contract
// with the Projects app, which ships as a plain script in a theme app extension
// and cannot import from this module graph. It is how the saved-projects drawer
// changes an amount using the shop's real box rules instead of its own guess.
window.STDPQty = QtyMath;

if (DEV) {
  window.theme = theme;
  console.debug('[theme] core.js loaded', Object.keys(theme));
}

export default theme;
